import LocalStorage from 'Core/commons/LocalStorage';

const localStorageNamespace = 'listing_context';

function sortColumns(columns) {
    const defaultCols = columns.filter((column) => column.isDefault);
    const visibleCols = columns
        .filter((column) => column.isVisible && !column.isDefault)
        .sort((a, b) => a.order - b.order);
    const hiddenCols = columns
        .filter((column) => !column.isVisible)
        .sort((a, b) => a.label.localeCompare(b.label));

    return [...defaultCols, ...visibleCols, ...hiddenCols].map((column, index) => ({
        ...column,
        order: index,
    }));
}

export default {
    namespaced: true,
    state() {
        return {
            listingName: '',
            listingLocalStorage: {},
            columns: [],
            pagination: {
                limit: 25,
            },
            sorting: {},
            defaultColumns: [],
            defaultPagination: {
                limit: 25,
                offset: '',
                total: 0,
                currentPage: 1,
                count: 0,
            },
            defaultSorting: {
                order: '',
                direction: '',
            },
        };
    },
    getters: {
        getListingName: (state) => state.listingName,
        getColumns: (state) => state.columns,
        getPagination: (state) => state.pagination,
        getSorting: (state) => state.sorting,
        getDefaultColumns: (state) => state.defaultColumns,
        getDefaultPagination: (state) => state.defaultPagination,
        getDefaultSorting: (state) => state.defaultSorting,
        getPageCount: (state) => state.pageCount,
    },
    mutations: {
        setListingName: (state, payload) => {
            state.listingName = payload;
        },
        setColumns: (state, payload) => {
            state.columns = payload;
        },
        setPagination: (state, payload) => {
            state.pagination = payload;
        },
        setSorting: (state, payload) => {
            state.sorting = payload;
        },
        setDefaultColumns: (state, payload) => {
            state.defaultColumns = payload;
        },
        setDefaultPagination: (state, payload) => {
            state.defaultPagination = payload;
        },
        setDefaultSorting: (state, payload) => {
            state.defaultSorting = payload;
        },
        setListingLocalStorage: (state, payload) => {
            state.listingLocalStorage = payload;
        },
        setPageCount: (state, payload) => {
            state.pageCount = payload;
        },
    },
    actions: {
        initListingSettingsStore({ commit, rootState, state }, initialsDatas) {
            const defaultColumns = initialsDatas.defaultColumns ?? state.defaultColumns;
            const defaultPagination =
                initialsDatas.defaultPaginationColumns ?? state.defaultPagination;
            const defaultSorting = initialsDatas.defaultSorting ?? state.defaultSorting;
            const listingName = initialsDatas.listingName
                ? `${initialsDatas.listingName}_${rootState.user.id}`
                : '';

            commit('setListingName', listingName);
            commit('setDefaultColumns', defaultColumns);
            commit('setDefaultPagination', defaultPagination);
            commit('setDefaultSorting', defaultSorting);
            commit('setColumns', sortColumns(defaultColumns));
            commit('setPagination', defaultPagination);
            commit('setSorting', defaultSorting);

            commit(
                'setListingLocalStorage',
                new LocalStorage(localStorageNamespace, state.listingName),
            );

            const listingStorageValues = state.listingLocalStorage.get();
            if (listingStorageValues) {
                const columnsWithStorage = defaultColumns.map((column) => {
                    const storedColumn = listingStorageValues.meta?.columns?.find(
                        (stored) => stored.code === column.code,
                    );
                    return storedColumn
                        ? { ...column, ...storedColumn, label: column.label }
                        : column;
                });
                commit('setColumns', sortColumns(columnsWithStorage));
                commit(
                    'setPagination',
                    listingStorageValues.meta?.limit
                        ? { limit: listingStorageValues.meta.limit }
                        : defaultPagination,
                );
                commit('setSorting', listingStorageValues.meta?.sorting ?? defaultSorting);
            }
        },
        updateColumns({ commit, dispatch }, columns) {
            const sorted = sortColumns(columns);
            commit('setColumns', sorted);

            const sortedColumn = sorted.find((column) => !!column.sortable?.direction);
            if (sortedColumn) {
                commit('setSorting', {
                    order: sortedColumn.sortable.orderBy,
                    direction: sortedColumn.sortable.direction,
                });
            }
            dispatch('updateLocalStorage', { columns: sorted });
        },
        updatePagination({ commit, dispatch }, pagination) {
            commit('setPagination', pagination);
            dispatch('updateLocalStorage', { limit: pagination.limit });
        },
        updateSorting({ commit, dispatch }, sorting) {
            commit('setSorting', sorting);
            dispatch('updateLocalStorage', { sorting });
        },
        updateLocalStorage({ state }, value) {
            const listingStorageValues = state.listingLocalStorage.get();
            state.listingLocalStorage.set({
                meta: {
                    ...(listingStorageValues?.meta ?? {}),
                    ...value,
                },
            });
        },
        resetColumns({ commit, getters, dispatch }) {
            const defaultColumns = getters.getDefaultColumns;
            const sorted = sortColumns(defaultColumns);
            commit('setColumns', sorted);
            dispatch('updateLocalStorage', { columns: null });
        },
        resetPagination({ commit, getters }) {
            commit('setPageCount', 1);
            commit('setPagination', {
                limit: getters.getPagination.limit,
                currentPage: 1,
            });
        },

        updatePageCount({ commit }, pageCount) {
            commit('setPageCount', pageCount);
        },
    },
};
